import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiResult, RequestOptions } from '@/services/apiClient';
import { sessionScopeKey, useSessionScopeFence } from './useSessionScope';

export function useScopedOperationalQuery<T>(feature: string, permission: string, loader: (options: RequestOptions) => Promise<ApiResult<T>>, interval = 60_000) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const query = useQuery({
    queryKey: ['settings-operation', feature, ...sessionScopeKey(identity)], enabled, placeholderData: () => undefined,
    queryFn: async ({ signal }) => {
      if (!isCurrent(identity)) throw new Error('Current access is required to load this view.');
      const result = await loader({ signal, sessionOrigin: identity.sessionOrigin });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Access changed while this view was loading.');
      if (!result.ok || !result.data) throw new Error(result.error ?? `Unable to load ${feature}`);
      return result.data;
    },
    retry: false, staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: 'always', refetchInterval: interval,
  });
  return { ...query, data: enabled && !query.isError ? query.data : undefined };
}

export function useScopedOperationalMutation<TInput, TOutput>(feature: string, permission: string, action: (input: TInput, options: RequestOptions) => Promise<ApiResult<TOutput>>) {
  const { identity, enabled, isCurrent } = useSessionScopeFence(permission);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['settings-operation-mutation', feature, ...sessionScopeKey(identity)], retry: false,
    onMutate: () => identity,
    mutationFn: async (input: TInput) => {
      if (!enabled || !isCurrent(identity)) throw new Error('Access changed. Reload before running this action.');
      try {
        const result = await action(input, { sessionOrigin: identity.sessionOrigin });
        if (!isCurrent(identity)) throw new Error('The originating session changed. Reload to check the action outcome.');
        if (!result.ok || !result.data) throw new Error(result.error ?? `Unable to run ${feature}`);
        return result.data;
      } catch (error) {
        // eslint-disable-next-line preserve-caught-error -- Prior-session errors must not escape through Error.cause after access changes.
        if (!isCurrent(identity)) throw new Error('The originating session changed. Reload to check the action outcome.');
        throw error;
      }
    },
    onSuccess: async (_data, _input, origin) => {
      if (origin && isCurrent(origin)) await client.invalidateQueries({ queryKey: ['settings-operation', feature, ...sessionScopeKey(origin)] });
    },
  });
  const visible = mutation.context && isCurrent(mutation.context);
  return { ...mutation, data: visible ? mutation.data : undefined, error: visible ? mutation.error : null, isPending: Boolean(visible && mutation.isPending) };
}
